import { Map } from 'leaflet';

/**
 * @param sampleInput M 1100 312 L 1196 264 L 1292 312 L 1292 408 L 1196 456 L 1100 408
 * @param sampleOutput [[1100,312],[1196,264],[1292,312],[1292,408],[1196,456],[1100,408]]
 */
export const svgPathToPixel = (path: string) => {
  const pathWithoutM = path.substring(2);
  const pixelCoords = pathWithoutM
    .split('L')
    .map(coord => coord.trim().split(' ').map(Number));
  return pixelCoords;
};

/**
 * @param sampleInput [[1100,312],[1196,264]]
 * @param sampleOutput [
 *  {lat: 0.01441955551185579, lng: 0.004119873046875001},
 *  {lat: 0.016479491960279726, lng: 0.008239746093750002},
 * ]
 */
export const pixelToLatLng = (pixelCoords: number[][], map: Map | null) => {
  if (map) {
    const latLng = pixelCoords.map((coords: number[]) =>
      map.containerPointToLatLng([coords[0], coords[1]]),
    );
    return latLng;
  }
  return null;
};

/**
 * @param sampleInput [
 *  {lat: 0.01441955551185579, lng: 0.004119873046875001},
 *  {lat: 0.016479491960279726, lng: 0.008239746093750002},
 * ]
 * @param sampleOutput [
 *  [0.01441955551185579, 0.012359619140625002],
 *  [0.016479491960279726, 0.016479492187500003],
 * ]
 */
export const latLngToCoords = (latLng: L.LatLng[] | null) => {
  if (latLng) {
    const coords = latLng.map(ll => [ll.lat, ll.lng]);
    return coords;
  }
  return null;
};

/**
 * @param sampleInput [
 *  [59.89548127325464, 10.625152587890627],
 *  [59.89651447802047, 10.6292724609375]
 * ]
 * @param sampleOutput [
 *  [10.625152587890627, 59.89548127325464],
 *  [10.6292724609375, 59.89651447802047]
 * ]
 */
export const coordsToGeoJSON = (coords: number[][] | null) => {
  if (coords) {
    const geoJSONCoords = coords?.map(coord => [coord[1], coord[0]]);
    return geoJSONCoords;
  }
  return null;
};

/**
 * @param sampleInput [
 *  [10.625152587890627, 59.89548127325464],
 *  [10.6292724609375, 59.89651447802047]
 * ]
 * @param sampleOutput [
 *  [59.89548127325464, 10.625152587890627],
 *  [59.89651447802047, 10.6292724609375]
 * ]
 */
export const geoJSONToCoords = (geoJSONCoords: number[][] | null) => {
  if (geoJSONCoords) {
    const coords = geoJSONCoords?.map(geoJSONCoord => [
      geoJSONCoord[1],
      geoJSONCoord[0],
    ]);
    return coords;
  }
  return null;
};

/**
 * https://en.wikipedia.org/wiki/Haversine_formula
 * The haversine formula determines the great-circle distance between two points on a sphere given their longitudes and latitudes.
 */
export const coordsToMeters = (
  bounds1: number[],
  bounds2: number[],
): number => {
  const [lat1, lon1] = bounds1;
  const [lat2, lon2] = bounds2;
  // generally used geo measurement function
  const R = 6378.137; // Radius of earth in KM
  const dLat = (lat2 * Math.PI) / 180 - (lat1 * Math.PI) / 180;
  const dLon = (lon2 * Math.PI) / 180 - (lon1 * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d * 1000; // meters
};
